import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DollarOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { formatSubscriptionAmount } from "../subscriptionFormatters";

const { Text } = Typography;
const { TextArea } = Input;

interface BillingSettings {
  id: number;
  default_warehouse_monthly_price: number;
  currency: string;
  annual_discount_months: number;
  default_credit_limit: number;
  default_grace_days: number;
  default_targeted_days_before_read_only: number;
  default_read_only_days_before_suspension: number;
  default_trial_read_only_days: number;
  default_payment_terms_days: number;
  access_enforcement_enabled: boolean;
  updated_at?: string | null;
}

interface BillingSettingsForm extends BillingSettings {
  revision_reason: string;
}

const SubscriptionBillingSettings: React.FC = () => {
  const [form] = Form.useForm<BillingSettingsForm>();
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Admin.Souscription.Plans.manage");

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/subscription/billing-settings", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(
          payload.message || "Impossible de charger le modèle de facturation",
        );
      }
      const nextSettings = payload.data.settings as BillingSettings;
      setSettings(nextSettings);
      form.setFieldsValue({ ...nextSettings, revision_reason: "" });
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Impossible de charger le modèle de facturation",
      );
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(
    async (values: BillingSettingsForm) => {
      setSaving(true);
      try {
        const response = await fetch("/api/subscription/billing-settings", {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...values, annual_discount_months: 1 }),
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(
            payload.message || "Impossible d'enregistrer les paramètres",
          );
        }
        const nextSettings = payload.data.settings as BillingSettings;
        setSettings(nextSettings);
        form.setFieldsValue({ ...nextSettings, revision_reason: "" });
        message.success("Modèle de facturation mis à jour");
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible d'enregistrer les paramètres",
        );
      } finally {
        setSaving(false);
      }
    },
    [form],
  );

  return (
    <Card
      bordered={false}
      loading={loading}
      title={
        <Space>
          <DollarOutlined />
          <span>Modèle de facturation Warehouse</span>
        </Space>
      }
      extra={
        <Tag
          color={settings?.access_enforcement_enabled ? "success" : "processing"}
          icon={<SafetyCertificateOutlined />}
        >
          {settings?.access_enforcement_enabled
            ? "Seuils financiers appliqués"
            : "Seuils financiers en observation"}
        </Tag>
      }
    >
      <Alert
        showIcon
        type="info"
        message="Un tarif unique, indépendant des plans"
        description={
          <span>
            Chaque Warehouse actif porte un tarif mensuel, regroupé selon la
            périodicité définie sur l'entreprise. Les plans ne portent désormais
            que les fonctionnalités et limites. Les états
            d'alerte, restrictions ciblées, lecture seule et suspension financière peuvent être
            observés avant activation. Une couverture expirée applique toujours
            la lecture seule puis la limitation au portail de souscription.
          </span>
        }
        style={{ marginBottom: 18 }}
      />

      {settings && (
        <Text type="secondary">
          Référence actuelle : {formatSubscriptionAmount(
            settings.default_warehouse_monthly_price,
            settings.currency,
          )} par Warehouse et par mois.
        </Text>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={saveSettings}
        style={{ marginTop: 18 }}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="default_warehouse_monthly_price"
              label="Tarif mensuel par Warehouse (XOF)"
              rules={[{ required: true, message: "Tarif requis" }]}
            >
              <InputNumber min={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="annual_discount_months"
              label="Mois offerts par période annuelle"
              rules={[{ required: true, message: "Remise requise" }]}
            >
              <InputNumber disabled min={1} max={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="default_payment_terms_days"
              label="Échéance de paiement (jours)"
              rules={[{ required: true, message: "Délai requis" }]}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="default_credit_limit"
              label="Plafond à compte par défaut (XOF)"
              rules={[{ required: true, message: "Plafond requis" }]}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
              <Form.Item
                name="default_grace_days"
                label="Tolérance avant restrictions ciblées (jours)"
              rules={[{ required: true, message: "Délai requis" }]}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="default_targeted_days_before_read_only"
                label="Restrictions ciblées avant lecture seule (jours)"
                rules={[{ required: true, message: "Délai requis" }]}
              >
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="default_read_only_days_before_suspension"
              label="Lecture seule avant suspension (jours)"
              rules={[{ required: true, message: "Délai requis" }]}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="default_trial_read_only_days"
              label="Lecture seule après essai (jours)"
              rules={[{ required: true, message: "Délai requis" }]}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="access_enforcement_enabled"
              label="Appliquer les seuils financiers"
              valuePropName="checked"
            >
              <Switch checkedChildren="Actif" unCheckedChildren="Observation" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="revision_reason"
          label="Motif de la modification"
          rules={[
            { required: true, whitespace: true, message: "Motif obligatoire" },
            { min: 3, max: 500, message: "Entre 3 et 500 caractères" },
          ]}
        >
          <TextArea
            rows={2}
            maxLength={500}
            showCount
            placeholder="Expliquez la décision commerciale ou opérationnelle."
          />
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!canManage}
        >
          Enregistrer les paramètres
        </Button>
      </Form>
    </Card>
  );
};

export default SubscriptionBillingSettings;
